import { AIPlatform, PlatformConfig } from './types';

/**
 * Configuration for each AI platform
 */
const PLATFORM_CONFIGS: Record<AIPlatform, PlatformConfig> = {
  chatgpt: {
    name: 'ChatGPT',
    baseUrl: 'https://chatgpt.com/',
    supportsPromptPrefill: true,
    urlParameter: 'q'
  },
  claude: {
    name: 'Claude',
    baseUrl: 'https://claude.ai/new',
    supportsPromptPrefill: true,
    urlParameter: 'q'
  },
  gemini: {
    name: 'Gemini',
    baseUrl: 'https://gemini.google.com/app',
    supportsPromptPrefill: false
  },
  perplexity: {
    name: 'Perplexity',
    baseUrl: 'https://www.perplexity.ai/search/',
    supportsPromptPrefill: true,
    urlParameter: 'q'
  },
  grok: {
    name: 'Grok',
    baseUrl: 'https://grok.com/',
    supportsPromptPrefill: true,
    urlParameter: 'q'
  }
};

/**
 * Platform adapter for AI integrations
 */
export class PlatformAdapter {
  /**
   * Get configuration for a specific platform
   */
  static getConfig(platform: AIPlatform): PlatformConfig {
    return PLATFORM_CONFIGS[platform];
  }

  /**
   * Get all available platforms
   */
  static getAllPlatforms(): AIPlatform[] {
    return Object.keys(PLATFORM_CONFIGS) as AIPlatform[];
  }

  /**
   * Get platforms that support prompt prefilling
   */
  static getPrefillSupportedPlatforms(): AIPlatform[] {
    return this.getAllPlatforms().filter(
      platform => PLATFORM_CONFIGS[platform].supportsPromptPrefill
    );
  }

  /**
   * Launch a platform with the given prompt
   */
  static launchPlatform(platform: AIPlatform, prompt: string): void {
    const config = this.getConfig(platform);
    
    if (config.supportsPromptPrefill && config.urlParameter) {
      // Launch platform with prompt prefilled in URL
      const encodedPrompt = encodeURIComponent(prompt);
      const launchUrl = `${config.baseUrl}?${config.urlParameter}=${encodedPrompt}`;
      window.open(launchUrl, '_blank');
    } else {
      // Fallback: open platform and copy prompt to clipboard
      window.open(config.baseUrl, '_blank');
    }
    
    // Always copy to clipboard as backup
    this.copyToClipboard(prompt);
  }

  /**
   * Copy text to clipboard
   */
  static copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers or non-secure contexts
      return new Promise((resolve, reject) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          textArea.remove();
          resolve();
        } catch (error) {
          textArea.remove();
          reject(error);
        }
      });
    }
  }

  /**
   * Generate a launch URL for a platform (without actually launching)
   */
  static generateLaunchUrl(platform: AIPlatform, prompt: string): string {
    const config = this.getConfig(platform);
    
    if (config.supportsPromptPrefill && config.urlParameter) {
      const encodedPrompt = encodeURIComponent(prompt);
      return `${config.baseUrl}?${config.urlParameter}=${encodedPrompt}`;
    }
    
    return config.baseUrl;
  }

  /**
   * Check if a platform supports prompt prefilling
   */
  static supportsPrefill(platform: AIPlatform): boolean {
    return this.getConfig(platform).supportsPromptPrefill;
  }

  /**
   * Get display name for a platform
   */
  static getPlatformName(platform: AIPlatform): string {
    return this.getConfig(platform).name;
  }

  /**
   * Validate that a platform is supported
   */
  static isValidPlatform(platform: string): platform is AIPlatform {
    return platform in PLATFORM_CONFIGS;
  }

  /**
   * Filter platforms based on enabled list
   */
  static filterEnabledPlatforms(enabledPlatforms?: string[]): AIPlatform[] {
    if (!enabledPlatforms || enabledPlatforms.length === 0) {
      return this.getAllPlatforms();
    }
    
    return enabledPlatforms.filter(this.isValidPlatform);
  }
}
